import React from "react";
import { Box, Typography, Divider, Alert } from "@mui/material";
import AppModal from "../common/AppModal";
import DetailItem from "../common/DetailItem";
import AppCircularProgress from "../common/AppCircularProgress";
import { ParentGuardianResponseDto } from "../../shared/dtos/parentGuardian/ParentGuardianResponseDto";

interface ViewParentModalProps {
  open: boolean;
  onClose: () => void;
  parent: ParentGuardianResponseDto | null;
  loading: boolean;
  error?: string | null;
}

const ViewParentModal: React.FC<ViewParentModalProps> = ({
  open,
  onClose,
  parent,
  loading,
  error = null,
}) => {
  const renderContent = (): React.ReactNode => {
    if (loading) {
      return (
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            my: 5,
          }}
        >
          <AppCircularProgress />
          <Typography sx={{ ml: 2, color: "text.secondary" }}>
            Loading Details...
          </Typography>
        </Box>
      );
    }

    if (error) {
      return (
        <Alert severity="error" sx={{ my: 2 }}>
          {error}
        </Alert>
      );
    }

    if (!parent) {
      return (
        <Typography sx={{ my: 2, color: "text.secondary" }}>
          Data not available.
        </Typography>
      );
    }

    return (
      <Box>
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
            gap: 2,
          }}
        >
          <DetailItem
            label="Full Name"
            value={`${parent.firstName || ""} ${parent.lastName || ""}`}
          />
          <DetailItem label="Email Address" value={parent.email || "N/A"} />
          <DetailItem
            label="Primary Phone"
            value={parent.primaryPhoneNumber || "N/A"}
          />
          <DetailItem label="Occupation" value={parent.occupation || "N/A"} />
        </Box>

        <Divider sx={{ my: 3 }} />

        <Typography variant="h6" sx={{ fontWeight: 700, mb: 1.5 }}>
          Address Information
        </Typography>

        {parent.address ? (
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
              gap: 2,
            }}
          >
            <DetailItem
              label="Street Line 1"
              value={parent.address.streetLine1}
            />
            <DetailItem
              label="Street Line 2"
              value={parent.address.streetLine2 || "N/A"}
            />
            <DetailItem label="City" value={parent.address.city} />
            <DetailItem
              label="State/Province"
              value={parent.address.stateOrProvince}
            />
            <DetailItem label="Postal Code" value={parent.address.postalCode} />
            <DetailItem label="Country" value={parent.address.country} />
          </Box>
        ) : (
          <Typography variant="body2" sx={{ color: "text.secondary" }}>
            No address information available.
          </Typography>
        )}
      </Box>
    );
  };

  return (
    <AppModal
      open={open}
      onClose={onClose}
      title="Parent / Guardian Details"
      width="md"
    >
      {renderContent()}
    </AppModal>
  );
};

export default ViewParentModal;
